import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSettings } from '../contexts/SettingsContext.tsx';
import {
  HomeIcon,
  BuildingLibraryIcon,
  TruckIcon,
  PaperAirplaneIcon,
  BanknotesIcon,
  DocumentChartBarIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserGroupIcon,
  CreditCardIcon,
  SwatchIcon,
  BuildingOfficeIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import { HomeModernIcon } from '@heroicons/react/24/outline';
import BoatIcon from './icons/BoatIcon.tsx';

// Função utilitária para construir URLs do backend
const getBackendUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

const Sidebar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { logout, user, hasPermission, hasResourceAccess } = useAuth();
  const { settings } = useSettings();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, resource: 'dashboard' },
    { name: 'Patrimônio', href: '/assets', icon: BuildingLibraryIcon, resource: 'assets' },
    { name: 'Aluguéis', href: '/rentals', icon: HomeModernIcon, resource: 'assets' },
    { name: 'Veículos', href: '/vehicles', icon: TruckIcon, resource: 'assets' },
    { name: 'Embarcações', href: '/vessels', icon: BoatIcon, resource: 'assets' },
    { name: 'Aeronaves', href: '/aircrafts', icon: PaperAirplaneIcon, resource: 'assets' },
    { name: 'Investimentos', href: '/investments', icon: BanknotesIcon, resource: 'investments' },
    { name: 'Filantropia', href: '/philanthropies', icon: HeartIcon, resource: 'investments' },
    { name: 'Empresas', href: '/companies', icon: BuildingOfficeIcon, resource: 'companies' },
    { name: 'Dívidas', href: '/debts', icon: CreditCardIcon, resource: 'debts' },
    { name: 'Recebíveis', href: '/receivables', icon: BanknotesIcon, resource: 'receivables' },
    { name: 'Relatórios', href: '/new-reports', icon: DocumentChartBarIcon, resource: 'reports' },
    { name: 'Usuários', href: '/users', icon: UserGroupIcon, permission: 'users.read' },
    { name: 'Whitelabel', href: '/whitelabel', icon: SwatchIcon, resource: 'whitelabel' },
    { name: 'Configurações', href: '/settings', icon: Cog6ToothIcon, permission: 'settings.read' },
  ];

  const canAccess = (item: any) => {
    if (item.permission) {
      return hasPermission(item.permission);
    }
    if (item.resource) {
      return hasResourceAccess(item.resource);
    }
    return true;
  };

  const handleSignOut = () => {
    logout();
  };

  return (
    <>
      {/* Mobile menu button */}
      <div className="md:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 bg-white shadow-lg"
        >
          {isMobileMenuOpen ? (
            <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
          ) : (
            <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black bg-opacity-50"
          onClick={() => setIsMobileMenuOpen(false)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsMobileMenuOpen(false);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Fechar menu"
        />
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div
          className={`flex flex-col transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-64'}`}
        >
          <div className="flex flex-col h-0 flex-1 bg-gradient-to-b from-primary-800 via-primary-900 to-secondary-900 shadow-xl relative">
            {/* Collapse/Expand Button */}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="absolute -right-3 top-6 z-10 bg-white rounded-full p-1.5 shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200"
            >
              {isCollapsed ? (
                <ChevronRightIcon className="h-4 w-4 text-gray-600" />
              ) : (
                <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
              )}
            </button>

            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div
                className={`flex items-center flex-shrink-0 px-4 mb-8 ${isCollapsed ? 'justify-center' : ''}`}
              >
                {settings?.logoUrl && !isCollapsed ? (
                  <img
                    src={
                      settings.logoUrl.startsWith('/uploads/')
                        ? getBackendUrl(settings.logoUrl)
                        : settings.logoUrl
                    }
                    alt={settings.companyName}
                    className="h-10 w-auto"
                  />
                ) : (
                  <div className="h-10 w-10 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-xl flex items-center justify-center shadow-lg">
                    <svg
                      className="h-6 w-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                )}
                {!isCollapsed && !settings?.logoUrl && (
                  <div className="ml-3">
                    <h1 className="text-white text-lg font-bold">
                      {settings?.companyName?.split(' ')[0] || 'Gestão'}
                    </h1>
                    <p className="text-primary-200 text-sm">
                      {settings?.companyName?.split(' ').slice(1).join(' ') || 'Patrimonial'}
                    </p>
                  </div>
                )}
              </div>
              <nav className={`mt-5 flex-1 space-y-2 ${isCollapsed ? 'px-2' : 'px-2'}`}>
                {navigation.map(item => {
                  if (!canAccess(item)) return null;

                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        `group flex items-center ${isCollapsed ? 'px-2 py-3 justify-center' : 'px-3 py-3'} text-sm font-medium rounded-xl transition-all duration-200 relative ${
                          isActive
                            ? 'bg-white/20 text-white shadow-lg backdrop-blur-sm border border-white/10'
                            : 'text-primary-100 hover:bg-white/10 hover:text-white'
                        }`
                      }
                      title={isCollapsed ? item.name : undefined}
                    >
                      <item.icon
                        className={`${isCollapsed ? '' : 'mr-3'} flex-shrink-0 h-5 w-5 transition-colors duration-200 ${({
                          isActive,
                        }: {
                          isActive: boolean;
                        }) => (isActive ? 'text-white' : 'text-primary-300')}`}
                        aria-hidden="true"
                      />
                      {!isCollapsed && item.name}
                      {isCollapsed && (
                        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                          {item.name}
                        </div>
                      )}
                    </NavLink>
                  );
                })}
              </nav>
            </div>
            <div
              className={`flex-shrink-0 flex border-t border-primary-700/50 p-4 ${isCollapsed ? 'justify-center' : ''}`}
            >
              <button
                onClick={handleSignOut}
                className={`flex-shrink-0 group block hover:bg-white/10 rounded-xl p-3 transition-all duration-200 ${isCollapsed ? '' : 'w-full'}`}
                title={isCollapsed ? 'Sair' : undefined}
              >
                <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
                  {(() => {
                    console.log('Sidebar - user.avatar:', user?.avatar);
                    console.log('Sidebar - user.avatar type:', typeof user?.avatar);
                    console.log('Sidebar - user object:', user);
                    return null;
                  })()}
                  {user?.avatar ? (
                    <img
                      src={
                        user.avatar &&
                        typeof user.avatar === 'string' &&
                        user.avatar.startsWith('/uploads/')
                          ? getBackendUrl(user.avatar)
                          : user.avatar
                      }
                      alt={user.name || 'Avatar'}
                      className="h-10 w-10 rounded-xl object-cover shadow-lg"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-primary-600 to-secondary-600 flex items-center justify-center text-white font-medium shadow-lg">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  {!isCollapsed && (
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-white">{user?.name || 'Usuário'}</p>
                      <div className="flex items-center text-xs text-primary-200">
                        <ArrowRightOnRectangleIcon className="h-3 w-3 mr-1" />
                        Sair
                      </div>
                    </div>
                  )}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      Sair
                    </div>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      <div
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-blue-800 via-blue-900 to-indigo-900 shadow-xl transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4 mb-8">
              <div className="h-10 w-10 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                <svg
                  className="h-6 w-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-white text-lg font-bold">Gestão</h1>
                <p className="text-primary-200 text-sm">Patrimonial</p>
              </div>
            </div>
            <nav className="mt-5 flex-1 px-2 space-y-2">
              {navigation.map(item => {
                if (!canAccess(item)) return null;

                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `group flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                        isActive
                          ? 'bg-white/20 text-white shadow-lg backdrop-blur-sm border border-white/10'
                          : 'text-blue-100 hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    <item.icon
                      className="mr-3 flex-shrink-0 h-5 w-5 text-blue-300"
                      aria-hidden="true"
                    />
                    {item.name}
                  </NavLink>
                );
              })}
            </nav>
          </div>
          <div className="flex-shrink-0 flex border-t border-blue-700/50 p-4">
            <button
              onClick={handleSignOut}
              className="flex-shrink-0 w-full group block hover:bg-white/10 rounded-xl p-3 transition-all duration-200"
            >
              <div className="flex items-center">
                {user?.avatar ? (
                  <img
                    src={
                      user.avatar &&
                      typeof user.avatar === 'string' &&
                      user.avatar.startsWith('/uploads/')
                        ? getBackendUrl(user.avatar)
                        : user.avatar
                    }
                    alt={user.name || 'Avatar'}
                    className="h-10 w-10 rounded-xl object-cover shadow-lg"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 flex items-center justify-center text-white font-medium shadow-lg">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                )}
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-white">{user?.name || 'Usuário'}</p>
                  <div className="flex items-center text-xs text-blue-200">
                    <ArrowRightOnRectangleIcon className="h-3 w-3 mr-1" />
                    Sair
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
